import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

const backgroundImages = [
  "/renamed_website_images/chemistry-lab-1.jpg",
  "/renamed_website_images/chemistry-lab-2.jpg",
  "/renamed_website_images/laboratory-gallery-01.jpg",
];

const cards = [
  {
    img: "/renamed_website_images/chemistry-lab-1.jpg",
    title: "Organic Chemistry",
    desc: "Explore carbon compounds, reactions, mechanisms, and hands-on organic experiments in our modern lab setup.",
    accent: "#3b82f6",
    border: "rgba(59,130,246,0.25)",
  },
  {
    img: "/renamed_website_images/chemistry-lab-2.jpg",
    title: "Inorganic Chemistry",
    desc: "Learn about elements, salts, metals, and conduct color reaction experiments to understand periodic trends.",
    accent: "#22c55e",
    border: "rgba(34,197,94,0.25)",
  },
  {
    img: "/renamed_website_images/laboratory-gallery-01.jpg",
    title: "Physical Chemistry & Titration",
    desc: "Dive into chemical kinetics, thermodynamics, and conduct lab-based evaluations to reinforce core theoretical principles.",
    accent: "#f97316",
    border: "rgba(249,115,22,0.25)",
  },
];

const ChemistryLab = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % backgroundImages.length,
      );
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen py-24 px-6 md:px-16 pt-36 md:pt-40 overflow-hidden bg-[#020810]">
      {/* Smooth Cross-fade Background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {backgroundImages.map((img, idx) => (
          <div
            key={idx}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
            style={{
              backgroundImage: `url('${img}')`,
              opacity: currentImageIndex === idx ? 1 : 0,
              transition: "opacity 1.5s ease-in-out",
            }}
          />
        ))}
      </div>

      {/* Dark overlay */}
      <div
        className="absolute inset-0 z-1"
        style={{ background: "rgba(2,8,16,0.88)" }}
      />

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Back link */}
        <Link
          to="/facilities"
          className="inline-flex items-center gap-2 text-sm font-bold tracking-wide mb-8 transition-colors duration-200"
          style={{ fontFamily: "Nunito, sans-serif", color: "#f4c842" }}
        >
          ← Back to Facilities
        </Link>

        <h1
          className="uppercase mb-4"
          style={{
            fontFamily: "Orbitron, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(1.8rem, 4vw, 3rem)",
            color: "#f0f5ff",
          }}
        >
          Chemistry Lab
        </h1>
        <div className="cosmic-divider mb-10" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {cards.map((card, i) => (
            <div
              key={i}
              className="glass-card overflow-hidden transition-all duration-300 border"
              style={{ borderColor: card.border }}
            >
              <div className="h-52 overflow-hidden">
                <img
                  src={card.img}
                  alt={card.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-5">
                <h3
                  className="font-bold mb-2 text-base"
                  style={{
                    fontFamily: "Cinzel, serif",
                    color: card.accent,
                  }}
                >
                  {card.title}
                </h3>
                <p
                  className="text-xs leading-relaxed"
                  style={{ fontFamily: "Nunito, sans-serif", color: "#8899b8" }}
                >
                  {card.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Navigation Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-white/10">
          <Link
            to="/facilities/biology-lab"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full glass-card text-xs uppercase tracking-widest text-gold hover:border-gold transition-all"
          >
            <FaArrowLeft /> Previous: Biology Lab
          </Link>
          <Link
            to="/facilities/physics-lab"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full cosmic-btn-gold text-xs uppercase tracking-widest"
          >
            Next: Physics Lab <FaArrowRight />
          </Link>
        </div>
      </div>

      {/* Side floating arrows */}
      <Link
        to="/facilities/biology-lab"
        aria-label="Previous Lab: Biology Lab"
        className="fixed z-50 left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowLeft />
      </Link>
      <Link
        to="/facilities/physics-lab"
        aria-label="Next Lab: Physics Lab"
        className="fixed z-50 right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowRight />
      </Link>
    </section>
  );
};

export default ChemistryLab;
